import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import axiosInstance from './axios';

// API/Components
import { AnalysisResultToStateColor, DATA_ERROR_VALUE, isEmpty, formatDateTime, isValidValue, measUnitsConversion, userMetricUnits } from './enums';

// Material UI
import Button from '@mui/material/Button';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Grid from '@mui/material/Grid';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
	zoomPlugin,
);

// Graph style
const GRAPH_STYLE = {
	AREA: 0,
	LINE: 1,
	POINT: 2
};

export default function UnitTrendGraph(props) {
	const { trendData, graphType, loading } = props;

	const { locationNum } = useParams();
	
	const chartRef = useRef(null);
	
	const [graphData, setGraphData] = useState([]);
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
	});
	const [graphStyle, setGraphStyle] = useState(GRAPH_STYLE.AREA);
	
	const [limitData, setLimitData] = useState([]);
	const [graphOptions, setGraphOptions] = useState({
		maintainAspectRatio: false,
		animation: false,
		transitions: {
			zoom: {
				animation: {
					duration: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				intersect: false,
				// axis: 'x',
				mode: 'index',
			},
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				limits: {
					x: {
						minRange: 2,
					},
					y: {
						min: 0,
						max: 100,
						minRange: 5,
					}
				},
				zoom: {
					mode: 'y',
					wheel: {
						enabled: true,
						speed: 0.02,
					},
					pinch: {
						enabled: true,
					},
					onZoomStart: (e) => {
						if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
							return;
						}
						if (e.event.shiftKey === true) {
							e.chart.config.options.plugins.zoom.zoom.mode = "x";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
						} else {
							e.chart.config.options.plugins.zoom.zoom.mode = "y";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
						}
					},
				}
			},
		},
	});
	
	// const [isLoading, setIsLoading] = useState(loading);

	// Keep track of the limits data for drawing the limit lines
	const limitDataRef = useRef(null);
	useEffect(() => {
		limitDataRef.current = limitData;
	}, [limitData]);
	
	// Keep track of the graph type for drawing limit lines
	const graphTypeRef = useRef(null);
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);
	
	// Keep track of the graph settings for drawing limit lines
	const graphSettingsRef = useRef(null);
	useEffect(() => {
		graphSettingsRef.current = graphSettings;
	}, [graphSettings]);
	
	// Get the list of limit data for the specified location id, string number, and unit number
	useEffect(() => {
		axiosInstance.get(`/loc/${locationNum}/limits`).then(response => {
			setLimitData(response.data);
		});
	}, [locationNum]);

	// Update the graph data sets
	useEffect(() => {
		if (typeof trendData != "undefined" && trendData !== null) {

			const systemData = GetGraphData(trendData, graphType);
			if (systemData !== undefined && systemData !== null) {
				setGraphData(systemData);
			}
		}
	}, [trendData, graphType, graphSettings, graphStyle, limitData]);

	useEffect(() => {
		const graphScales = SetGraphYScale(graphType);
		if (graphScales !== undefined || graphScales !== null) {
			setGraphOptions((prevState) => {
				return {
					...prevState,
					scales: {
						y: graphScales,
						x: {
							title: {
								display: false,
							}
						}
					}
				}
			});
		}
	}, [limitData, graphType]);
	
	useEffect(() => {
			const graphZoom = SetGraphZoom(graphType);
		if (graphZoom !== undefined || graphZoom !== null) {
			setGraphOptions((prevState) => {
				return {
					...prevState,
					plugins: {
						...prevState.plugins,
						zoom: {
							...prevState.plugins.zoom,
							limits: {
								...prevState.plugins.zoom.limits,
								y: graphZoom,
							},
						},
					},
				}
			});
		}
	}, [limitData, graphType]);

	const graphPlugins = [
		{
			afterDraw: chart => {				
				
				// Draw a vertical dashed line at the tooltip's x position
				let ctx = chart.ctx;
				if (chart.tooltip?._active?.length) {
					let x = chart.tooltip._active[0].element.x;
					let yAxis = chart.scales.y;
					ctx.save();
					ctx.beginPath();
					ctx.setLineDash([5, 5]);
					ctx.moveTo(x, yAxis.top);
					ctx.lineTo(x, yAxis.bottom);
					ctx.lineWidth = 1;
					ctx.strokeStyle = 'rgba(0, 0, 255, 0.9)';
					ctx.stroke();
					ctx.restore(); 
				}
			}
		},
		{
			afterDatasetDraw: chart => {
				let graphType = graphTypeRef.current;
				let graphSettings = graphSettingsRef.current;
				let limitData = limitDataRef.current;

				if (limitData != undefined && limitData != null && !isEmpty(limitData)) {
					// If enabled, draw a horizontal dashed line at the various limit values
					if (graphSettings.limitsEnabled) {
						const limits = GetLimitsData(limitData, graphType);
						if (limits != undefined && limits != null && limits.length > 0) {
							limits.forEach((limit) => {
								DrawHorizontalDashedLine(chart, limit.value, limit.color);
							});
						}
					}
				}
			}
		}
	];
		
	function DrawHorizontalDashedLine(chart, yValue, color) {
		if (chart !== undefined && yValue !== undefined && yValue > DATA_ERROR_VALUE.INVALID_VALUE) {
			let ctx = chart.ctx;
			let xAxis = chart.scales.x;
			let y = chart.scales.y.getPixelForValue(yValue);

			ctx.save();
			ctx.beginPath();
			ctx.setLineDash([5, 5]);
			ctx.moveTo(xAxis.left, y);
			ctx.lineTo(xAxis.right, y);
			ctx.lineWidth = 1;
			ctx.strokeStyle = color;
			ctx.stroke();
			ctx.restore(); 
		}
	}

	function GetLimitsData(limitData, graphType) {
		let critHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let critLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		
		if (limitData != undefined && limitData != null && !isEmpty(limitData) &&
			graphType != undefined && graphType != null) {
			switch(graphType) {
				case 0:
					critHiLimit = limitData.volt.hi_crit;
					critLoLimit = limitData.volt.lo_crit;
					break;
				case 1:
					critHiLimit = limitData.current;
					break;
				case 2:
					critHiLimit = measUnitsConversion(limitData.temp.hi_crit);
					critLoLimit = measUnitsConversion(limitData.temp.lo_crit);
					break;
				default:
					critHiLimit = limitData.volt.hi_crit;
					critLoLimit = limitData.volt.lo_crit;
					break;
			}
		}

		// Get the associated limit colors
		let hiCritColor = AnalysisResultToStateColor(4);
		let loCritColor = AnalysisResultToStateColor(5);

		let limitSets = [];
		if (critHiLimit !== undefined && critHiLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critHiLimit,
				color: hiCritColor
			});
		}

		if (critLoLimit !== undefined && critLoLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critLoLimit,
				color: loCritColor
			});
		}
		
		return limitSets;
	}

	function SetGraphYScale(graphType) {
		
		if (limitData != undefined && limitData != null && !isEmpty(limitData)) {

			let max = 0;
			let min = 50;
			let title = "";

			switch (graphType) {
				case 0:  // Voltage
					let upperVolt = limitData.volt.hi_crit;
					if (upperVolt !== undefined && upperVolt > DATA_ERROR_VALUE.INVALID_VALUE) {
						max = upperVolt * 1.05;
					}

					let lowerVolt = limitData.volt.lo_crit;
					if (lowerVolt !== undefined && lowerVolt > DATA_ERROR_VALUE.INVALID_VALUE) {
						min = lowerVolt * 0.80;
					}

					title = "System Voltage (VDC)";
					break;

				case 1:  // Current
					let current = limitData.current;
					if (current !== undefined && current > DATA_ERROR_VALUE.INVALID_VALUE) {
						max = current * 1.15;
					}

					min = 0;

					title = "System Current (A)";
					break;

				case 2:  // Temperature
					let upperTemp = limitData.temp.hi_crit;
					if (upperTemp !== undefined && upperTemp > DATA_ERROR_VALUE.INVALID_VALUE) {
						max = measUnitsConversion(upperTemp) * 1.15;
					}

					let lowerTemp = limitData.temp.lo_crit;
					if (lowerTemp !== undefined && lowerTemp > DATA_ERROR_VALUE.INVALID_VALUE) {
						min = measUnitsConversion(lowerTemp) * 0.85;
					}
					
					let units = userMetricUnits() ? '°C' : '°F';
					title = 'Ambient Temperature (' + units + ')';
					break;

				default:  // Voltage
					upperVolt = limitData.volt.hi_crit;
					if (upperVolt !== undefined && upperVolt > DATA_ERROR_VALUE.INVALID_VALUE) {
						max = upperVolt * 1.05;
					}

					lowerVolt = limitData.volt.lo_crit;
					if (lowerVolt !== undefined && lowerVolt > DATA_ERROR_VALUE.INVALID_VALUE) {
						min = lowerVolt * 0.80;
					}

					title = "System Voltage (VDC)";
					break;
			}

			if (max > 0 && min != 50) {
				return {
					max: max,
					min: min,
					title: {
						display: true,
						text: title,
						font: {
							family: "Saira, sans-serif",
							size: 18,
							weight: 600,
						},
					},
				};
			}
		}

		return {};
	}
	
	function SetGraphZoom(graphType) {

		switch(graphType) {
			case 0:  // Voltage
				return {
					min: 0,
					max: 650,
					minRange: 1,
				};
			case 1:  // Current
				return {
					min: 0,
					max: 1000,
					minRange: 5,
				};

			case 2:  // Temperature
				return {
					min: measUnitsConversion(-40),
					max: measUnitsConversion(75),
					minRange: 5,
				};

			default:
				return {
					min: 0,
					max: 100,
					minRange: 5,
				};
		}
	}

	function GetMeasurementInfo(measurement, graphType) {
		let value, analysis = 0;
		switch(graphType) {
			case 0:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
			case 1:
				value = measurement.current.measurement?.toFixed(3);
				analysis = (measurement.current.analysis_valid) ? 
					measurement.current.analysis_result : 0;
				break;
			case 2:
				let temp = measurement.temperature.measurement?.toFixed(3);
				value = measUnitsConversion(temp);
				analysis = (measurement.temperature.analysis_valid) ? 
					measurement.temperature.analysis_result : 0;
				break;
			default:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
		}
		value = (value !== null && value !== undefined && value > -4000000) ? value : 0;
		let analysisColor = AnalysisResultToStateColor(analysis);

		return {value, analysisColor};
	}

	function GetGraphTypeText(graphType) {
		switch(graphType) {
			case 0:
				return "System Voltage Trend";
			case 1:
				return "System Current Trend";
			case 2:
				return "Ambient Temperature Trend";
			default:
				return "System Voltage Trend";
		}
	}
	
	function GetGraphData(trendData, graphType) {
		// Pick the units to graph based on the selected string
		if (trendData && trendData.length > 0) {
			
			const filteredData = trendData.map((measurement) => {
				const { value, analysisColor } = GetMeasurementInfo(measurement, graphType);
				
				return {
					label: formatDateTime(measurement.measurement_date),
					value: value,
					barColor: analysisColor + "77",  // Make the bar color partially transparent
					borderColor: analysisColor,  // The outline should be fully opaque
				};
			});

			const graphTypeText = GetGraphTypeText(graphType);
			
			// Add measurement data to datasets
			let dataSets = []
			dataSets.push({
				type: graphStyle == GRAPH_STYLE.POINT ? 'scatter' : 'line',
				label: graphTypeText,
				backgroundColor: filteredData.map((measurement) => measurement.barColor),
				borderColor: filteredData.map((measurement) => measurement.borderColor),
				segment: {
					borderColor: (ctx) => (filteredData[ctx.p1DataIndex].barColor),
					backgroundColor: (ctx) => (filteredData[ctx.p1DataIndex].barColor),
				},
				data: filteredData.map((measurement) => measurement.value),
				fill: graphStyle == GRAPH_STYLE.AREA,
				order: 10,
			});

			return {
				labels: filteredData.map((measurement) => measurement.label),
				datasets: dataSets,
			};
		}

		return null;
	}

	const handleLimitMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			limitsEnabled: !graphSettings.limitsEnabled})
		);
	};

	const handleGraphResetZoom = () => {
		chartRef.current.resetZoom();
	};

	const handleGraphStyle = (event, newStyle) => {
		if (newStyle !== null) {
			setGraphStyle(newStyle);
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '99%',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			<div style={{ height: '100%', width: '100%' }}>
				{ (graphData !== undefined && graphData.length != 0) &&
					<Chart
						ref={chartRef}
						type='line'
						options={graphOptions}
						data={graphData}
						plugins = {graphPlugins}
					/>
				}
			</div>
			<Grid container spacing={1}>
				<Grid item xs alignItems="left">
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value="limitMarkers"
							selected={graphSettings.limitsEnabled}
							onClick={handleLimitMarkersToggle}
						>
							Limits
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs alignItems="center">
					<Button
						variant="contained"
						startIcon={<CenterFocusStrongIcon />}
						onClick={handleGraphResetZoom}
						sx={{
							marginY: 1,
							backgroundColor: 'white',
							border: '1px solid lightgray',
							color: 'black',
							":hover": {
								backgroundColor: 'lightgray',
								boxShadow: 'none',
							}

						}}
					>
						Reset View
					</Button>
				</Grid>
				<Grid item>
					<ToggleButtonGroup
						value={graphStyle}
						onChange={handleGraphStyle}
						exclusive
						aria-label="graph marker type"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value={GRAPH_STYLE.AREA}
							aria-label="Area Graph Style"
						>
							Area
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.LINE}
							aria-label="Line Graph Style"
						>
							Line
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.POINT}
							aria-label="Point Graph Style"
						>
							Point
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
			</Grid>
		</div>
	);
}